import clsx from 'clsx';
import { formatDateSafe, formatDateTimeSafe } from '@/utils/dateUtils';
import type { VacationTimelineEvent } from '../../../utils/vacationTimeline';
import { NO_DATE_LABEL } from '../vacationRequestDetailsModal.definitions';

const DOT_TONE_CLASSES: Record<VacationTimelineEvent['tone'], string> = {
    done: 'border-emerald-300 bg-emerald-50',
    pending: 'border-amber-300 bg-amber-50',
    rejected: 'border-red-300 bg-red-50',
    muted: 'border-gray-200 bg-gray-50',
};

const DOT_INNER_TONE_CLASSES: Record<VacationTimelineEvent['tone'], string> = {
    done: 'bg-emerald-500',
    pending: 'bg-amber-500',
    rejected: 'bg-red-500',
    muted: 'bg-gray-400',
};

interface RequestTimelineItemProps {
    event: VacationTimelineEvent;
    isLast: boolean;
}

function RequestTimelineItem({ event, isLast }: RequestTimelineItemProps) {
    const dateLabel = event.occurredAt
        ? event.hasTime
            ? formatDateTimeSafe(event.occurredAt)
            : formatDateSafe(event.occurredAt)
        : NO_DATE_LABEL;

    return (
        <li className="relative flex gap-4 pb-6 last:pb-0">
            {!isLast && (
                <div className="absolute left-[15px] top-8 h-full w-0.5 bg-gray-200" aria-hidden="true" />
            )}

            <div
                className={clsx(
                    'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2',
                    DOT_TONE_CLASSES[event.tone]
                )}
            >
                <div className={clsx('h-3 w-3 rounded-full', DOT_INNER_TONE_CLASSES[event.tone])} />
            </div>

            <div className="flex-1 pt-0.5">
                <p className="text-sm font-semibold text-raro-gray-900 font-montserrat">
                    {event.label}
                </p>
                <p className="mt-0.5 text-xs text-raro-gray-500 font-montserrat">{dateLabel}</p>
                {event.actorEmail && (
                    <p className="mt-0.5 break-all text-xs text-raro-gray-500 font-montserrat">
                        {event.actorEmail}
                    </p>
                )}
                {event.note && (
                    <p className="mt-2 rounded-lg bg-raro-surface-50 p-3 text-sm text-raro-gray-700 font-montserrat">
                        {event.note}
                    </p>
                )}
            </div>
        </li>
    );
}

interface RequestTimelineProps {
    events: VacationTimelineEvent[];
    labelledBy?: string;
}

export function RequestTimeline({ events, labelledBy }: RequestTimelineProps) {
    return (
        <ol className="relative" aria-labelledby={labelledBy}>
            {events.map((event, index) => (
                <RequestTimelineItem
                    key={`${event.key}-${index}`}
                    event={event}
                    isLast={index === events.length - 1}
                />
            ))}
        </ol>
    );
}

export default RequestTimeline;
